const compareText = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'es', {
    numeric: true,
    sensitivity: 'base'
  });

const relationshipLabels = {
  SPOUSE: 'Cónyuge',
  COHABITANT: 'Conviviente',
  CHILD: 'Hijo(a)',
  PARENT: 'Padre/Madre',
  GRANDCHILD: 'Nieto(a)',
  SIBLING: 'Hermano(a)',
  OTHER: 'Otro'
};

export const getDependantRelationshipLabel = (dependant) => {
  const relationship =
    dependant.relationship || dependant.employeeDependant?.relationship;
  return (
    relationshipLabels[relationship] ||
    relationship ||
    dependant.relationshipLabel ||
    'Sin informar'
  );
};

/**
 * Groups the coverages of an individual incorporation ticket by policy.
 *
 * Items are the same objects received (not copies), so a selection changed
 * from the grouped table is reflected in the original coverages list used
 * to validate and submit the ticket.
 *
 * Selection counters are not part of the groups (see countSelectedCoverages
 * and countPendingCoverages): computing them here would rebuild the groups
 * on every selection change and collapse the expanded policies.
 *
 * @param {Array} coverages - Rows with numberPolicy, groupName, nameCoverage,
 *   required and selected (true, false or '' when still pending).
 * @returns {Array} Policy groups with `coverages` items.
 */
export const groupCoveragesByPolicy = (coverages = []) => {
  const groups = new Map();
  coverages.forEach((coverage) => {
    const numberPolicy = coverage.numberPolicy || '-';
    if (!groups.has(numberPolicy)) {
      groups.set(numberPolicy, {
        key: `policy-${numberPolicy}`,
        numberPolicy,
        coverages: []
      });
    }
    groups.get(numberPolicy).coverages.push(coverage);
  });

  return [...groups.values()]
    .map((group) => {
      const items = [...group.coverages].sort(
        (a, b) =>
          compareText(a.groupName, b.groupName) ||
          compareText(a.nameCoverage, b.nameCoverage)
      );
      return {
        ...group,
        coverages: items,
        coverageCount: items.length
      };
    })
    .sort((a, b) => compareText(a.numberPolicy, b.numberPolicy));
};

export const countSelectedCoverages = (group) =>
  (group?.coverages || []).filter((item) => item.selected === true).length;

export const countPendingCoverages = (group) =>
  (group?.coverages || []).filter((item) => item.selected === '').length;

/**
 * True while any coverage still waits for the broker Si/No choice.
 * Required rows arrive already selected, so only '' counts as pending.
 *
 * @param {Array} coverages - Rows with selected (true, false or '').
 * @returns {boolean} Whether Revisado must stay blocked.
 */
export const hasPendingBrokerCoverages = (coverages = []) =>
  coverages.some((item) => item.selected === '');

/**
 * Optional CoverageGroupCompany ids the broker marked Si. Required rows
 * travel on their own and never depend on this list.
 *
 * @param {Array} coverages - Rows with idCoverage, required and selected.
 * @returns {Array} Selected optional CoverageGroupCompany ids.
 */
export const getSelectedOptionalCoverageIds = (coverages = []) =>
  coverages
    .filter((item) => item.selected === true && !item.required)
    .map((item) => item.idCoverage);

/**
 * Groups the dependants of a ticket by dependant, with their selected
 * coverages as items.
 *
 * @param {Array} dependants - Ticket dependants from the store.
 * @param {Array} availableCoverages - Coverages available for the ticket.
 * @param {Object} [options]
 * @param {boolean} [options.defaultToAvailableCoverages] - Show every available
 *   coverage for staged dependants without a selection.
 * @returns {Array} Dependant groups with `coverages` items.
 */
export const groupTicketDependants = (
  dependants = [],
  availableCoverages = [],
  { defaultToAvailableCoverages = false } = {}
) =>
  dependants.map((dependant) => {
    const hasIncorporationSelections = Array.isArray(
      dependant.selectedCoverageIncorporationIds
    );
    // Mirrors the backend on execution: a staged dependant without any
    // selection gets every available coverage that allows dependants.
    const usesAvailableCoverages =
      defaultToAvailableCoverages &&
      dependant.isStaged &&
      !hasIncorporationSelections &&
      !Array.isArray(dependant.selectedCoverageIds);
    const selectedCoverageIds = usesAvailableCoverages
      ? availableCoverages.map((coverage) => coverage.id)
      : hasIncorporationSelections
        ? dependant.selectedCoverageIncorporationIds
        : dependant.selectedCoverageIds || [];
    const coverageKeys =
      usesAvailableCoverages || hasIncorporationSelections
        ? ['id', 'employeeCoverageIncorporationId']
        : dependant.isStaged
          ? ['coverageGroupCompanyId']
          : ['employeeCoverageId'];

    const coverages = selectedCoverageIds
      .map((selectedCoverageId) => {
        const coverage = availableCoverages.find((item) =>
          coverageKeys.some(
            (key) => Number(item[key]) === Number(selectedCoverageId)
          )
        );
        return {
          key: `${dependant.id}-${selectedCoverageId}`,
          policy: coverage?.policy?.numberPolicy || '-',
          group: coverage?.groupCompany?.name || '-',
          coverage:
            coverage?.coverage?.name || `Cobertura ${selectedCoverageId}`
        };
      })
      .sort(
        (a, b) =>
          compareText(a.policy, b.policy) ||
          compareText(a.group, b.group) ||
          compareText(a.coverage, b.coverage)
      );

    return {
      ...dependant,
      key: `dependant-${dependant.id}`,
      fullName: dependant.fullName || dependant.fullname || '',
      relationshipLabel: getDependantRelationshipLabel(dependant),
      coverages,
      coverageCount: coverages.length,
      policyNumbers: [...new Set(coverages.map((item) => item.policy))]
        .filter((policy) => policy !== '-')
        .sort(compareText)
    };
  });
